import { Attrs, NodeType } from 'prosemirror-model'
import { EditorState } from 'prosemirror-state'
import { DecorationSet, EditorView } from 'prosemirror-view'

import {
  DEFAULT_MORE_DISPLAY_TEXT,
  DEFAULT_SCENE_CONTD_FOOTER_PREFIX,
  DEFAULT_SCENE_CONTD_HEADER_PREFIX,
  uuid,
} from '#util'
import { HeaderOrFooterPrefs } from '#util/zodSchemas/printPreferences'

import { getSpeaker } from '../block-info/speakerLookup'
import { getConfigData } from '../configData'
import { BoundaryKind, buildContdText } from '../page-decoration'
import { buildPageDecorationData } from '../pagination/page-deco-adapter'
import { PageLockMap } from '../pagination/PageLockMap'
import {
  getLockedPageInventory,
  getPagination,
} from '../pagination/paginationPlugin'

import {
  buildPrintPaginationDecorations,
  PrintPageInfo,
} from './print-decorations'

interface SplitBoundary {
  insertPos: number
  needsSplit: boolean
  // Whether this boundary gets MORE/CONT'D markers (dialogue) vs none (plain
  // inline split of action/general).
  isDialogue: boolean
  // Page index receiving the (MORE) text.
  pageIndex: number
  // Page index receiving the CHARACTER (CONT'D) text.
  contdPageIndex: number
  speaker: string | undefined
  // Type/attrs of the node being split (dialogue, action, or general). Only
  // set when needsSplit is true.
  splitNodeType?: NodeType
  splitNodeAttrs?: Attrs
}

export interface DialogueSplitContext {
  getCollatedRevision?: (start: number, end: number) => string | undefined
  getPageColor?: (start: number, end: number) => string | undefined
  headerFooter?: {
    headers: HeaderOrFooterPrefs
    footers: HeaderOrFooterPrefs
    title?: string
    timestamp?: string
    date?: string
    duration?: string
    activeRevisionLabel?: string
  }
}

interface PrintPageRange {
  start: number
  end: number
  boundaryKind: BoundaryKind
  pageNumber: number
  pageAlias?: string
  isTitlePage?: boolean
  pageSection?: string
  dialogueMoreText?: string
  dialogueContdText?: string
  sceneContdHeader?: boolean
  sceneContdFooter?: boolean
  sceneNumber?: string
  sceneElementNumber?: string
}

function resolveSceneContdText(
  pageData: readonly PrintPageRange[],
  enableSceneContds: boolean | undefined,
): readonly { headerText?: string; footerText?: string }[] {
  return pageData.map((page) => ({
    headerText:
      enableSceneContds && page.sceneContdHeader
        ? DEFAULT_SCENE_CONTD_HEADER_PREFIX +
          (page.sceneNumber ? ` (${page.sceneNumber})` : '')
        : undefined,
    footerText:
      enableSceneContds && page.sceneContdFooter
        ? DEFAULT_SCENE_CONTD_FOOTER_PREFIX
        : undefined,
  }))
}

type PrintPageInfoMetadata = Pick<
  PrintPageInfo,
  'pageColor' | 'collatedRevision'
>

interface PrintDecorationOptions {
  pageData: readonly PrintPageRange[]
  pageRevisionInfo: readonly PrintPageInfoMetadata[]
  enableSceneContds: boolean | undefined
  headerFooter: DialogueSplitContext['headerFooter']
}

const EMPTY_HEADER_FOOTER_PREFS: HeaderOrFooterPrefs = {
  enabled: false,
  slots: {},
}

export function materializePrintPages(
  view: EditorView,
  ctx: DialogueSplitContext = {},
): DecorationSet {
  const initialPageData = getPrintPageData(view.state)
  if (!initialPageData) return DecorationSet.empty

  const pageRevisionInfo = initialPageData.map((page) => {
    if (page.isTitlePage) return {}
    return {
      pageColor: ctx.getPageColor?.(page.start, page.end),
      collatedRevision: ctx.getCollatedRevision?.(page.start, page.end),
    }
  })

  const pageData = splitProseNodes(view, initialPageData)
  const finalPageData = splitPageNodes(view, pageData)

  return buildPrintDecorations(view, {
    pageData: finalPageData,
    pageRevisionInfo,
    enableSceneContds: getConfigData(view.state).currentMoresContds
      ?.enableSceneContds,
    headerFooter: ctx.headerFooter,
  })
}

function getPrintPageData(state: EditorState) {
  const pagination = getPagination(state)
  if (!pagination) return undefined
  const { currentMoresContds } = getConfigData(state)

  const locking =
    getLockedPageInventory(state) ??
    PageLockMap.from(pagination.pages, state.doc)
  return buildPageDecorationData(state, pagination, locking, currentMoresContds)
}

function splitPageNodes(
  view: EditorView,
  pageData: readonly PrintPageRange[],
): readonly PrintPageRange[] {
  const tr = view.state.tr

  for (let i = pageData.length - 1; i >= 0; i--) {
    const page = pageData[i]
    const splitStart = page.start
    const $pos = view.state.doc.resolve(splitStart)

    if ($pos.parent.type !== view.state.schema.nodes.page) continue
    if ($pos.parentOffset === 0) continue

    tr.split(splitStart, 1)
  }

  if (!tr.docChanged) return pageData

  view.dispatch(tr)

  return pageData.map((page) => ({
    ...page,
    start: tr.mapping.map(page.start),
  }))
}

function buildPrintDecorations(
  view: EditorView,
  {
    pageData,
    pageRevisionInfo,
    enableSceneContds,
    headerFooter,
  }: PrintDecorationOptions,
): DecorationSet {
  const sceneContdText = resolveSceneContdText(pageData, enableSceneContds)

  const pages: PrintPageInfo[] = pageData.map((page, index) => ({
    ...pageRevisionInfo[index],
    start: page.start,
    pageNumber: page.pageNumber,
    pageAlias: page.pageAlias,
    isTitlePage: Boolean(page.isTitlePage),
    pageSection: page.pageSection,
    sceneElementNumber: page.sceneElementNumber,
    dialogueMoreText: page.dialogueMoreText,
    dialogueContdText: page.dialogueContdText,
    sceneContdHeaderText: sceneContdText[index]?.headerText,
    sceneContdFooterText: sceneContdText[index]?.footerText,
  }))

  const totalPages = pages.filter((page) => !page.isTitlePage).length

  return buildPrintPaginationDecorations(view.state.doc, pages, {
    headers: headerFooter?.headers ?? EMPTY_HEADER_FOOTER_PREFS,
    footers: headerFooter?.footers ?? EMPTY_HEADER_FOOTER_PREFS,
    script: {
      title: headerFooter?.title,
      timestamp: headerFooter?.timestamp,
      date: headerFooter?.date,
      duration: headerFooter?.duration,
      activeRevisionLabel: headerFooter?.activeRevisionLabel,
      totalPages,
    },
  })
}

// Splits the prose node straddling a page boundary into two sibling nodes of
// the same type, so splitPageNodes() can later split the page itself there.
// Handles dialogue (which also gets MORE/CONT'D markers) as well as plain
// inline splits (action/general), which get no markers.
function splitProseNodes(
  view: EditorView,
  pageData: readonly PrintPageRange[],
): readonly PrintPageRange[] {
  const { doc } = view.state

  const boundaries: SplitBoundary[] = []
  for (let i = 0; i < pageData.length - 1; i++) {
    const kind = pageData[i].boundaryKind

    if (kind === BoundaryKind.DialogueContinuation) {
      const splitPos = pageData[i].end
      const dialogueNode = doc.resolve(splitPos).parent

      boundaries.push({
        insertPos: splitPos,
        needsSplit: true,
        isDialogue: true,
        pageIndex: i,
        contdPageIndex: i + 1,
        speaker: getSpeaker(view.state, splitPos)?.toUpperCase(),
        splitNodeType: dialogueNode.type,
        splitNodeAttrs: dialogueNode.attrs,
      })
    } else if (kind === BoundaryKind.AdjacentDialogues) {
      const boundaryPos = pageData[i].end
      boundaries.push({
        insertPos: boundaryPos,
        needsSplit: false,
        isDialogue: true,
        pageIndex: i,
        contdPageIndex: i + 1,
        speaker: getSpeaker(view.state, boundaryPos - 1)?.toUpperCase(),
      })
    } else if (kind === BoundaryKind.InlineSplit) {
      const splitPos = pageData[i].end
      const splitNode = doc.resolve(splitPos).parent

      boundaries.push({
        insertPos: splitPos,
        needsSplit: true,
        isDialogue: false,
        pageIndex: i,
        contdPageIndex: i + 1,
        speaker: undefined,
        splitNodeType: splitNode.type,
        splitNodeAttrs: splitNode.attrs,
      })
    }
  }

  if (boundaries.length === 0) return pageData

  const { tr } = view.state
  const dialogueMarkers = new Map<
    number,
    { moreText?: string; contdText?: string }
  >()

  for (let i = boundaries.length - 1; i >= 0; i--) {
    const boundary = boundaries[i]
    if (boundary.needsSplit && boundary.splitNodeType) {
      tr.split(boundary.insertPos, 1, [
        {
          type: boundary.splitNodeType,
          attrs: { ...boundary.splitNodeAttrs, id: uuid() },
        },
      ])
    }

    if (!boundary.isDialogue) continue

    dialogueMarkers.set(boundary.pageIndex, {
      ...dialogueMarkers.get(boundary.pageIndex),
      moreText: DEFAULT_MORE_DISPLAY_TEXT,
    })
    dialogueMarkers.set(boundary.contdPageIndex, {
      ...dialogueMarkers.get(boundary.contdPageIndex),
      contdText: buildContdText(boundary.speaker),
    })
  }

  view.dispatch(tr)

  const updatedPageData = getPrintPageData(view.state) ?? []

  return updatedPageData.map((page, index) => ({
    ...page,
    dialogueMoreText: dialogueMarkers.get(index)?.moreText,
    dialogueContdText: dialogueMarkers.get(index)?.contdText,
  }))
}
